'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Link } from '@/i18n/routing';
import { useAppSelector } from '@/lib/redux/hooks';
import { selectUsageData } from '@/lib/redux/slices/usageSlice';
import userServices from '@/services/users-services';
import { ArrowRight, Brain, FileSearch, Scale } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

/**
 * Welcome modal shown once to new trial users on their first visit.
 * Backend-controlled via onboarding.show_welcome_modal in /usage.
 *
 * Design: SOTA single-screen onboarding (no modes, no limits — value-first).
 * Modes are discovered contextually in the chat toolbar.
 */
export function TrialWelcomeModal() {
  const t = useTranslations('TrialWelcome');
  const usageData = useAppSelector(selectUsageData);

  const showWelcomeModal = usageData?.onboarding?.show_welcome_modal ?? false;
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  // This component is mounted in the ROOT locale layout, so without a gate it
  // fires on whatever page the user happens to be on when the backend first
  // reports `show_welcome_modal` — i.e. immediately after the account is
  // created, wherever that happens.
  //
  // That became a real problem once the advocate profile-claim flow started
  // provisioning the account itself: the modal landed on top of
  // /najdite-odbornika/narokovat/verify, covering the terms checkbox and the
  // "Aktivovať profil" button at the exact moment of conversion. Same hazard
  // on /mfa/enroll, which is a mandatory step for advocate accounts.
  //
  // The copy is chat-centric ("Premýšľajúci a Hĺbkový AI chat", CTA to /chat),
  // so the chat page is where it belongs and where it is actually actionable.
  // Anywhere else it is an interruption, not onboarding.
  //
  // Matches /chat and its session routes (/chat/<id>) across locale prefixes
  // (/sk/chat, /en/chat).
  const isChatPage = pathname?.includes('/chat') ?? false;

  useEffect(() => {
    if (!showWelcomeModal || !isChatPage) return undefined;
    const timer = setTimeout(() => setIsOpen(true), 800);
    return () => clearTimeout(timer);
  }, [showWelcomeModal, isChatPage]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    userServices.dismissOnboarding().catch(() => {});
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='max-w-[400px] bg-surface-base border border-line-default p-6'>
        <DialogHeader className='space-y-2 text-center'>
          <div className='mx-auto w-10 h-10 rounded-xl bg-brand-tertiary/10 flex items-center justify-center'>
            <Scale className='w-5 h-5 text-brand-tertiary' />
          </div>
          <DialogTitle className='text-lg font-semibold text-content-primary'>
            {t('title')}
          </DialogTitle>
          <DialogDescription className='text-sm text-content-secondary leading-relaxed'>
            {t('subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className='my-4 border-t border-line-subtle' />

        {/* Value propositions — what you can DO */}
        <div className='space-y-3'>
          {[
            { icon: FileSearch, text: t('benefit1') },
            { icon: Scale, text: t('benefit2') },
            { icon: Brain, text: t('benefit3') },
          ].map(({ icon: Icon, text }, i) => (
            <div key={i} className='flex items-start gap-3'>
              <div className='mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-brand-tertiary/10 flex items-center justify-center'>
                <Icon className='w-3 h-3 text-brand-tertiary' />
              </div>
              <p className='text-sm text-content-secondary leading-snug'>
                {text}
              </p>
            </div>
          ))}
        </div>

        <div className='my-4 border-t border-line-subtle' />

        {/* CTA — next-intl Link auto-localizes /chat to /sk/chat */}
        <div className='space-y-3'>
          <Button
            variant='brandGradient'
            size='lg'
            className='w-full min-h-[48px] text-sm font-semibold'
            asChild
          >
            <Link href='/chat' onClick={handleClose}>
              {t('cta')}
              <ArrowRight className='w-4 h-4 ml-2' />
            </Link>
          </Button>
          <p className='text-center text-xs text-content-muted'>
            {t('ctaNote')}
          </p>
        </div>

        {/* Mode hint */}
        <p className='text-center text-[11px] text-content-muted/60 mt-2'>
          {t('modeHint')}
        </p>
      </DialogContent>
    </Dialog>
  );
}
